import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
      Loading...
    </div>
  </div>
);

/**
 * Route guard for any page that requires authentication.
 * Use `requireRole={["ADMIN"]}` for admin-only routes.
 * Use `requireSubscription={"AUTO_TRADING"}` for paid routes.
 */
export const ProtectedRoute = ({ children, requireRole, requireSubscription, requireActive = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  if (user.is_deleted) {
    return <Navigate to="/?auth_error=Account+disabled" replace />;
  }
  // Phase 46 fix — don't redirect to /profile-setup if we're already there
  // (caused a blank screen for new users since the route itself is guarded).
  if (!user.profile_complete && location.pathname !== "/profile-setup") {
    return <Navigate to="/profile-setup" replace />;
  }
  // 2026-09-12 — mandatory legal-acceptance gate (Terms & Conditions,
  // Privacy Policy, Risk Disclosure, Algo-Trading Disclosure), inserted
  // right after profile_complete and before the active/pending check so a
  // PENDING user still has to accept before reaching /pending. Gated on
  // `user.profile_complete` being true first — otherwise a brand-new user
  // (profile_complete=false, legal_accepted=false) sitting on /profile-setup
  // gets bounced to /legal-acceptance, which then bounces them straight back
  // to /profile-setup (its own profile_complete check above), forever —
  // the same blank-screen redirect-loop class the "Phase 46 fix" comment
  // above already fixed once for /profile-setup itself. Terms are only
  // shown AFTER profile setup completes (ProfileSetup.jsx's submit handler
  // navigates to /legal-acceptance itself once profile_complete is true).
  if (user.profile_complete && !user.legal_accepted && location.pathname !== "/legal-acceptance") {
    return <Navigate to="/legal-acceptance" replace />;
  }
  if (requireActive && user.status !== "ACTIVE") {
    return <Navigate to="/pending" replace />;
  }
  if (requireRole && !requireRole.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  if (
    requireSubscription &&
    user.role !== "ADMIN" &&
    user.role !== "POWER_USER" &&
    user.subscription !== requireSubscription &&
    !(requireSubscription === "TIPS" && user.subscription === "AUTO_TRADING")
  ) {
    return <Navigate to="/upgrade" replace />;
  }
  return children;
};
